import { newSpecPage } from '@stencil/core/testing';
import { NylasTimeslotPicker } from '../nylas-timeslot-picker';
import { h } from '@stencil/core';

describe('nylas-timeslot-picker', () => {
  beforeEach(() => {
    // Clear and reset the mock before each test if needed
    jest.clearAllMocks();
  });

  it('renders loading state correctly', async () => {
    const page = await newSpecPage({
      components: [NylasTimeslotPicker],
      html: `<nylas-timeslot-picker loading-state="true"></nylas-timeslot-picker>`,
    });

    expect(page.root).toMatchSnapshot();
    expect(page?.root?.shadowRoot?.querySelector('.loading')).not.toBeNull();
  });

  it('displays no availability message correctly', async () => {
    const page = await newSpecPage({
      components: [NylasTimeslotPicker],
      html: `<nylas-timeslot-picker></nylas-timeslot-picker>`,
    });

    if (!page.root) {
      throw new Error('Component did not render.');
    }

    page.root.availability = [];
    await page.waitForChanges();
    const emptyState = page?.root?.shadowRoot?.querySelector('.empty') as HTMLDivElement;

    expect(emptyState.textContent).toContain('No dates available');
  });

  it('emits timeslotSelected event with timeslot details on selection', async () => {
    const mockTimeslot = {
      start_time: new Date(),
      end_time: new Date(new Date().getTime() + 3600000), // 1 hour later
    };
    const page = await newSpecPage({
      components: [NylasTimeslotPicker],
      html: `<nylas-timeslot-picker></nylas-timeslot-picker>`,
    });

    if (!page.root) {
      throw new Error('Component did not render.');
    }

    page.root.availability = [mockTimeslot];
    await page.waitForChanges();

    const spyEvent = jest.fn();
    page.win.addEventListener('timeslotSelected', spyEvent);

    const timeslotButton = page?.root?.shadowRoot?.querySelector('button') as HTMLButtonElement;
    timeslotButton.click();

    await page.waitForChanges();

    expect(spyEvent).toHaveBeenCalledWith(
      expect.objectContaining({
        detail: mockTimeslot,
      }),
    );
  });

  it('emits timeslotConfirmed event with selected timeslot on confirm', async () => {
    const mockTimeslot = {
      start_time: new Date(),
      end_time: new Date(new Date().getTime() + 3600000), // 1 hour later
    };
    const page = await newSpecPage({
      components: [NylasTimeslotPicker],
      html: `<nylas-timeslot-picker></nylas-timeslot-picker>`,
    });

    if (!page.root) {
      throw new Error('Component did not render.');
    }

    page.root.availability = [mockTimeslot];
    page.root.selectedTimeslot = mockTimeslot;
    await page.waitForChanges();

    const spyEvent = jest.fn();
    page.win.addEventListener('timeslotConfirmed', spyEvent);

    const confirmButton = page?.root?.shadowRoot?.querySelector('.footer button-component') as HTMLButtonElement;
    confirmButton.click();

    await page.waitForChanges();

    expect(spyEvent).toHaveBeenCalled();
    expect(spyEvent.mock.calls[0][0].detail.timeslot).toEqual(mockTimeslot);
  });

  it('renders timeslots for selected date', async () => {
    const selectedDate = new Date();
    const availability = [
      {
        start_time: new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), 10, 0),
        end_time: new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), 11, 0),
      },
    ];
    const page = await newSpecPage({
      components: [NylasTimeslotPicker],
      template: () => {
        return (
          <nylas-timeslot-picker
            themeConfig={{
              '--nylas-primary': '#000000',
            }}
          ></nylas-timeslot-picker>
        );
      },
    });
    if (!page.root) {
      throw new Error('Component did not render.');
    }

    page.root.selectedDate = selectedDate;
    page.root.availability = availability;
    await page.waitForChanges();
    expect(page.root).toMatchSnapshot();

    const timeslotButtons = page?.root?.shadowRoot?.querySelectorAll('button.time') as NodeListOf<HTMLButtonElement>;
    expect(timeslotButtons.length).toBe(availability.length);
  });

  it('renders timeslots based on the selected timezone', async () => {
    // The timeslots below are in UTC
    const availability = [
      {
        emails: ['pooja.g@nylas.com'],
        end_time: new Date(2025, 0, 4, 19, 30),
        start_time: new Date(2025, 0, 4, 19, 0),
      },
      {
        emails: ['pooja.g@nylas.com'],
        end_time: new Date(2025, 0, 4, 20, 0),
        start_time: new Date(2025, 0, 4, 19, 30),
      },
      {
        emails: ['pooja.g@nylas.com'],
        end_time: new Date(2025, 0, 4, 20, 30),
        start_time: new Date(2025, 0, 4, 20, 0),
      },
      {
        emails: ['pooja.g@nylas.com'],
        end_time: new Date(2025, 0, 4, 21, 0),
        start_time: new Date(2025, 0, 4, 20, 30),
      },
      {
        emails: ['pooja.g@nylas.com'],
        end_time: new Date(2025, 0, 4, 21, 30),
        start_time: new Date(2025, 0, 4, 21, 0),
      },
      {
        emails: ['pooja.g@nylas.com'],
        end_time: new Date(2025, 0, 4, 22, 0),
        start_time: new Date(2025, 0, 4, 21, 30),
      },
      {
        emails: ['pooja.g@nylas.com'],
        end_time: new Date(2025, 0, 4, 22, 30),
        start_time: new Date(2025, 0, 4, 22, 0),
      },
      {
        emails: ['pooja.g@nylas.com'],
        end_time: new Date(2025, 0, 4, 23, 0),
        start_time: new Date(2025, 0, 4, 22, 30),
      },
      {
        emails: ['pooja.g@nylas.com'],
        end_time: new Date(2025, 0, 4, 23, 30),
        start_time: new Date(2025, 0, 4, 23, 0),
      },
    ];
    const selectedDate = new Date(2025, 0, 4);
    const page = await newSpecPage({
      components: [NylasTimeslotPicker],
      template: () => {
        return (
          <nylas-timeslot-picker
            themeConfig={{
              '--nylas-primary': '#000000',
            }}
            selectedDate={selectedDate}
          ></nylas-timeslot-picker>
        );
      },
    });
    if (!page.root) {
      throw new Error('Component did not render.');
    }

    page.root.availability = availability;
    page.root.selectedTimezone = 'America/Toronto';
    await page.waitForChanges();
    expect(page.root).toMatchSnapshot();
  });
});
